import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { DOMAIN_COLORS, TYPE_COLORS } from '@/lib/constants';
import { ArchiveRestore, Trash2 } from 'lucide-react';
import { useConfirmation } from '@/components/shared/ZCard';
import OverflowMenu from '@/components/shared/OverflowMenu';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import Logotype from '@/components/shared/Logotype';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function SoulSyncCodexArchive() {
  const navigate = useNavigate();
  const feedback = useConfirmation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // entry id awaiting second tap

  useEffect(() => {
    loadArchived();
  }, []);

  const loadArchived = async () => {
    try {
      const { data } = await api.get('/soulsync/entries?status=archived&limit=200');
      setEntries(data);
    } catch { /* silent — empty state handles it */ }
    finally { setLoading(false); }
  };

  const restoreEntry = async (id) => {
    // Optimistic: remove from archive list immediately
    const prev = entries;
    setEntries(entries.filter(e => e.id !== id));
    try {
      await api.put(`/soulsync/entries/${id}`, { status: 'active' });
      feedback.confirm();
    } catch {
      setEntries(prev);
      feedback.fail();
    }
  };

  const hardDelete = async (id) => {
    // Optimistic: remove from archive list immediately
    const prev = entries;
    setEntries(entries.filter(e => e.id !== id));
    setDeleteConfirm(null);
    try {
      await api.delete(`/soulsync/entries/${id}?hard=true`);
      feedback.confirm();
    } catch {
      setEntries(prev);
      feedback.fail();
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto pb-40">
      {/* Breadcrumb */}
      <div style={{ marginBottom: 20 }}>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate('/soulsync')}
                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Logotype app="soulsync" size="sidebar" />
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate('/soulsync')}
                style={{ cursor: 'pointer', color: '#6b7084', fontSize: 13, fontFamily: "'Inter', sans-serif" }}
              >
                Codex
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage style={{ color: '#f0f0f5', fontSize: 13, fontFamily: "'Inter', sans-serif" }}>
                Archive
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <ZLoadingBar accent="#4B5FD4" loading={loading} />

      {!loading && entries.length === 0 && (
        <ZEmptyState
          mark="S"
          accent="#4B5FD4"
          message="No archived entries"
          hint="Archived entries will appear here. They can be restored or permanently deleted."
        />
      )}

      {entries.map((entry) => {
        const domainColor = DOMAIN_COLORS[entry.domain] || '#b0b4c0';
        const isConfirming = deleteConfirm === entry.id;

        return (
          <div
            key={entry.id}
            style={{
              position: 'relative',
              background: 'rgba(255,255,255,0.03)',
              border: '0.5px solid rgba(255,255,255,0.06)',
              borderLeft: `2px solid ${domainColor}`,
              borderRadius: 10,
              padding: 14,
              marginBottom: 8,
              opacity: 0.7,
            }}
          >
            {!isConfirming && (
              <OverflowMenu
                items={[
                  { icon: <ArchiveRestore size={16} strokeWidth={1.5} />, label: 'Restore', onClick: () => restoreEntry(entry.id) },
                  { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete permanently', onClick: () => setDeleteConfirm(entry.id), destructive: true },
                ]}
              />
            )}

            {/* Entry content */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
              <div style={{
                fontSize: 13, fontWeight: 500, color: '#f0f0f5',
                fontFamily: 'Georgia, serif', lineHeight: 1.4,
                flex: 1, overflow: 'hidden',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}>
                {entry.title}
              </div>
              <div style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 3,
                background: DOMAIN_COLORS[entry.domain],
              }} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{
                background: `${TYPE_COLORS[entry.type]}18`, color: TYPE_COLORS[entry.type],
                fontSize: 9, padding: '2px 7px', borderRadius: 4, fontWeight: 500,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                {entry.type}
              </span>
              {(entry.threads || []).slice(0, 2).map(t => (
                <span key={t} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(255,255,255,0.04)', color: '#7A7A9A' }}>
                  #{t}
                </span>
              ))}
              <span style={{ fontSize: 10, color: '#b0b4c0', fontFamily: 'monospace', marginLeft: 'auto' }}>
                {formatDate(entry.created_at)}
              </span>
            </div>

            {/* Inline destructive confirmation (7.1 pattern) */}
            {isConfirming && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
                paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.04)',
              }}>
                <button
                  onClick={() => hardDelete(entry.id)}
                  style={{
                    background: '#E24B4A', border: 'none', borderRadius: 8,
                    color: '#ffffff', fontFamily: "'Inter', sans-serif",
                    fontSize: 12, fontWeight: 600, padding: '8px 16px',
                    cursor: 'pointer',
                  }}
                >
                  Delete permanently
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  style={{
                    background: 'none', border: 'none',
                    color: '#6b7084', fontFamily: "'Inter', sans-serif",
                    fontSize: 12, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
